import { useState } from "react";
import { Mail, Lock, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/flowly/infra/Button/Button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "@/lib/toast";
import { SIGNUPS_ENABLED } from "@/config/featureFlags";
import { AuthModalGoogleButton } from "./AuthModalGoogleButton";

export function AuthModalLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const { signIn, signInWithGoogle } = useAuth();
  const { closeAuthModal, switchAuthMode, openVerifyEmailModal } = useAuthModal();
  const { t } = useLanguage();

  const handleResetPassword = async () => {
    if (!email) {
      toast.error(t.auth.enterEmailForReset, {
        description: t.auth.enterEmailDescription,
      });
      return;
    }

    setIsResetLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });

    if (error) {
      toast.error(t.auth.resetEmailError, {
        description: error.message,
      });
    } else {
      toast.success(t.auth.resetEmailSent, {
        description: t.auth.checkInbox,
      });
    }
    setIsResetLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      if (error.message === "Email not confirmed") {
        closeAuthModal();
        openVerifyEmailModal(email, false);
      } else {
        toast.error(t.auth.loginError, {
          description:
            error.message === "Invalid login credentials"
              ? t.auth.invalidCredentials
              : error.message,
        });
      }
    } else {
      toast.success(t.auth.loginSuccess);
      closeAuthModal();
    }

    setIsLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    const { error } = await signInWithGoogle();
    if (error) {
      toast.error(t.auth.googleLoginError, {
        description: error.message,
      });
      setIsGoogleLoading(false);
    }
    // Don't set loading to false on success - will redirect
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">{t.auth.welcomeBack}</h2>
        <p className="text-muted-foreground text-sm">{t.auth.continueToCreate}</p>
      </div>

      <AuthModalGoogleButton
        onClick={handleGoogleSignIn}
        isLoading={isGoogleLoading}
        label={t.auth.continueWithGoogle}
      />

      <div className="relative my-6">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
          {t.auth.or}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="login-email" className="text-start block">
            {t.auth.email}
          </Label>
          <div className="relative">
            <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="login-email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pr-10 text-left"
              dir="ltr"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="login-password" className="text-start block">
            {t.auth.password}
          </Label>
          <div className="relative">
            <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="login-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10 text-left"
              dir="ltr"
              required
              minLength={6}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={handleResetPassword}
            disabled={isResetLoading}
            className="text-sm text-primary hover:underline !p-0"
          >
            {isResetLoading ? t.auth.sending : t.auth.forgotPassword}
          </Button>
        </div>

        <Button type="submit" variant="primary" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t.auth.loggingIn}
            </>
          ) : (
            t.auth.loginTitle
          )}
        </Button>
      </form>

      {SIGNUPS_ENABLED && (
        <div className="mt-6 text-center">
          <p className="text-muted-foreground text-sm">
            {t.auth.noAccountQuestion}{" "}
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={switchAuthMode}
              className="text-primary hover:underline font-medium !p-0"
            >
              {t.auth.signupTitle}
            </Button>
          </p>
        </div>
      )}
    </div>
  );
}
